import moment from 'moment'
import { Card, Col, Table, Button } from 'react-bootstrap'
import React, { useState, useEffect } from 'react'
import { useOrganizationIntegrations } from 'hooks/use-organization-integrations'
import useOrganizationSettings from 'hooks/use-organization-settings'
import IntegrationConnectionDetailModal from 'components/integration-connection-detail-modal'

interface IntegrationConnectionSettings {
  id?: number
  create_new_contacts?: boolean
  two_way_sync?: boolean
  log_against_deals?: boolean
  sync_call_recordings?: boolean
  sync_outgoing_answered_calls?: boolean
  sync_outgoing_unanswered_calls?: boolean
  sync_incoming_answered_calls?: boolean
  sync_incoming_missed_calls?: boolean
  sync_outgoing_voicemails?: boolean
  sync_incoming_sms?: boolean
  sync_outgoing_sms?: boolean
  sync_contacts?: boolean
  sync_calls?: boolean
  sync_messages?: boolean
  sync_email_messages?: boolean
  sync_email_open_events?: boolean
  sync_contact_notes?: boolean
  last_sync_at?: string
  zapier_api_key?: string
  webhook_url?: string
}

interface ConfiguredIntegration {
  id: number
  name: string
  type: string
  status: string
  enabled: boolean
  connected_at: string
  // GHL-specific fields
  ghl_company_id?: string
  ghl_location_id?: string
  state?: string
  version?: number
  sync_as_lead?: boolean
  // General integration fields
  webhook_url?: string
  instance_url?: string
  portal_id?: string
  tenant?: string
  // Integration connection settings
  settings?: IntegrationConnectionSettings
}

const OrganizationIntegrationsCard = ({
  orgId,
  organizationName,
  userName,
}: {
  orgId: string
  organizationName: string
  userName?: string
}) => {
  const { data: integrationData, isLoading: isLoadingIntegrations } =
    useOrganizationIntegrations(orgId)
  const { data: organizationSettings, isLoading: isLoadingSettings } =
    useOrganizationSettings({ orgId })
  const [showModal, setShowModal] = useState(false)
  const [selectedIntegration, setSelectedIntegration] =
    useState<ConfiguredIntegration | null>(null)

  const handleIntegrationClick = (integration: ConfiguredIntegration) => {
    setSelectedIntegration(integration)
    setShowModal(true)
  }

  const handleCloseModal = () => {
    setShowModal(false)
    setSelectedIntegration(null)
  }

  // Keep selectedIntegration in sync with query data
  useEffect(() => {
    if (selectedIntegration && integrationData?.integrations) {
      const updatedIntegration = integrationData.integrations.find(
        (i) => i.id === selectedIntegration.id
      )
      if (updatedIntegration) {
        setSelectedIntegration(updatedIntegration)
      }
    }
  }, [integrationData])

  return (
    <Col md="12">
      <Card className="mt-1">
        <Card.Body>
          {isLoadingIntegrations ? (
            <div className="text-center py-4">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
              <p className="mt-2 text-muted">
                Loading integration connections...
              </p>
            </div>
          ) : integrationData?.integrations &&
            integrationData.integrations.length > 0 ? (
            <div
              style={{
                overflowY: 'auto',
                overflowX: 'auto',
                maxHeight: '400px',
                width: '100%',
              }}
            >
              <div style={{ minWidth: '800px' }}>
                <Table responsive variant="dark" className="modern-table">
                  <thead>
                    <tr className="modern-table-header-row">
                      <th className="modern-table-header-cell">Name</th>
                      <th className="modern-table-header-cell">Type</th>
                      <th className="modern-table-header-cell">Status</th>
                      <th className="modern-table-header-cell">Enabled</th>
                      <th className="modern-table-header-cell">Connected At</th>
                    </tr>
                  </thead>
                  <tbody>
                    {integrationData.integrations.map((integration, index) => (
                      <tr
                        key={integration.id}
                        className={
                          index === 0
                            ? 'modern-table-first-row'
                            : 'modern-table-row-border'
                        }
                      >
                        <td className="modern-table-body-cell">
                          <button
                            type="button"
                            className="btn btn-link p-0"
                            style={{
                              textDecoration: 'none',
                              border: 'none',
                              background: 'none',
                              fontSize: 'inherit',
                              cursor: 'pointer',
                              color: '#87CEEB',
                            }}
                            onClick={() => handleIntegrationClick(integration)}
                          >
                            {integration.name}
                          </button>
                        </td>
                        <td className="modern-table-body-cell">
                          {integration.type}
                        </td>
                        <td className="modern-table-body-cell">
                          {integration.status}
                        </td>
                        <td className="modern-table-body-cell">
                          {integration.enabled ? 'Yes' : 'No'}
                        </td>
                        <td className="modern-table-body-cell">
                          {moment(integration.connected_at).format('LLL')}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
            </div>
          ) : (
            <p>
              {organizationName
                ? `${organizationName} has no integration connections`
                : `${userName} is not associated with any organization`}
            </p>
          )}
        </Card.Body>
      </Card>

      <IntegrationConnectionDetailModal
        show={showModal}
        handleClose={handleCloseModal}
        integration={selectedIntegration}
        organizationSettings={organizationSettings}
        orgId={orgId}
      />
    </Col>
  )
}

export default OrganizationIntegrationsCard
